/**
 * Loads the tenant theme and paints it onto <html>. One toggle, two complete palettes.
 *
 * Author: Danish Khan
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api } from './api.ts';
import { DEFAULT_THEME, applyTheme, preferredMode, THEME_STORAGE_KEY, type ThemeJson, type ThemeMode } from './theme.ts';

interface ThemeState {
  theme: ThemeJson;
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  toggle: () => void;
}

const Ctx = createContext<ThemeState | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }): ReactNode {
  const [theme, setTheme] = useState<ThemeJson>(DEFAULT_THEME);
  const [mode, setModeState] = useState<ThemeMode>(() => preferredMode());

  useEffect(() => {
    let live = true;
    void api
      .theme()
      .then((t) => {
        if (live) setTheme(t);
      })
      .catch(() => {
        /* a tenant with no theme keeps the house palette */
      });
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    applyTheme(theme, mode, document.documentElement);
  }, [theme, mode]);

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      /* the choice just will not survive a reload */
    }
  }, []);

  const toggle = useCallback(() => setMode(mode === 'dark' ? 'light' : 'dark'), [mode, setMode]);

  const value = useMemo<ThemeState>(() => ({ theme, mode, setMode, toggle }), [theme, mode, setMode, toggle]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTheme(): ThemeState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useTheme outside ThemeProvider');
  return ctx;
}
